import md5 from 'md5';

// Photo size configurations (must match backend)
const PHOTO_SIZES = {
  small: 64,
  medium: 128,
  large: 256,
} as const;

export type PhotoSize = 'small' | 'medium' | 'large';

interface PlayerPhotoData {
  profilePhotoType?: 'UPLOAD' | 'GRAVATAR' | string;
  profilePhotoUrl?: string | null;
  email?: string | null;
}

/**
 * Generate a Gravatar URL from an email address.
 *
 * @param email - The email address to generate the Gravatar URL for
 * @param size - The size in pixels (default: 128)
 * @returns The Gravatar URL
 */
export function getGravatarUrl(email: string, size: number = 128): string {
  const hash = md5(email.toLowerCase().trim());
  return `https://www.gravatar.com/avatar/${hash}?s=${size}&d=identicon`;
}

/**
 * Generate a player's profile photo URL client-side without API calls.
 * This eliminates the need for the two-step API call (config + url).
 *
 * @param player - Player data containing photo configuration
 * @param size - The desired photo size
 * @param token - Auth token for uploaded photo URLs
 * @returns The photo URL or null if no photo configured
 */
export function getPlayerPhotoUrl(
  player: PlayerPhotoData,
  size: PhotoSize,
  token: string
): string | null {
  // Handle Gravatar
  if (player.profilePhotoType === 'GRAVATAR' && player.email) {
    return getGravatarUrl(player.email, PHOTO_SIZES[size]);
  }

  // Handle uploaded photo
  if (player.profilePhotoType === 'UPLOAD' && player.profilePhotoUrl) {
    // Extract the base filename from the stored URL
    // profilePhotoUrl is stored as "uploads/player-photos/{filename}.webp"
    const photoUrl = player.profilePhotoUrl;

    // Get just the base filename without path and extension
    const pathParts = photoUrl.split('/');
    const filename = pathParts[pathParts.length - 1];
    const baseFilename = filename.replace(/\.[^.]+$/, ''); // Remove extension

    // Construct the API endpoint URL with size and token
    return `/api/v1/images/player-photos/${baseFilename}-${size}.webp?token=${encodeURIComponent(token)}`;
  }

  // No photo configured
  return null;
}

/**
 * Check if a player has a photo configured.
 *
 * @param player - Player data containing photo configuration
 * @returns true if the player has a photo configured
 */
export function hasPlayerPhoto(player: PlayerPhotoData): boolean {
  if (player.profilePhotoType === 'GRAVATAR' && player.email) {
    return true;
  }
  if (player.profilePhotoType === 'UPLOAD' && player.profilePhotoUrl) {
    return true;
  }
  return false;
}

interface UserPhotoData {
  profilePhotoType?: string;
  profilePhotoUrl?: string | null;
  gravatarEmail?: string | null;
  email?: string;
}

/**
 * Generate a user's profile photo URL client-side without API calls.
 * Matches the backend getUserProfilePhotoUrl logic.
 */
export function getUserPhotoUrl(
  user: UserPhotoData,
  size: PhotoSize,
  token: string
): string | null {
  if (user.profilePhotoType === 'GRAVATAR') {
    const email = user.gravatarEmail || user.email;
    if (email) return getGravatarUrl(email, PHOTO_SIZES[size]);
  }

  if (user.profilePhotoUrl) {
    // External URLs (social avatars) — use directly
    if (user.profilePhotoUrl.startsWith('http://') || user.profilePhotoUrl.startsWith('https://')) {
      return user.profilePhotoUrl;
    }

    // Local uploads — construct API endpoint URL
    const ext = user.profilePhotoUrl.match(/\.[^.]+$/)?.[0] || '.webp';
    const base = user.profilePhotoUrl.replace(/\.[^.]+$/, '');
    const pathParts = (size === 'large' ? user.profilePhotoUrl : `${base}-${size}${ext}`).split('/');
    const filename = pathParts[pathParts.length - 1];
    return `/api/v1/images/user-photos/${filename}?token=${encodeURIComponent(token)}`;
  }

  return null;
}

interface OrgPhotoData {
  orgProfilePhotoType?: string;
  orgProfilePhotoUrl?: string | null;
  orgGravatarEmail?: string | null;
}

/**
 * Generate an organization's profile photo URL client-side without API calls.
 * Matches the backend getProfilePhotoUrl logic.
 */
export function getOrgPhotoUrl(
  org: OrgPhotoData,
  size: PhotoSize
): string | null {
  if (org.orgProfilePhotoType === 'GRAVATAR' && org.orgGravatarEmail) {
    return getGravatarUrl(org.orgGravatarEmail, PHOTO_SIZES[size]);
  }

  if (org.orgProfilePhotoUrl) {
    const ext = org.orgProfilePhotoUrl.match(/\.[^.]+$/)?.[0] || '.webp';
    const base = org.orgProfilePhotoUrl.replace(/\.[^.]+$/, '');
    return `${base}-${size}${ext}`;
  }

  return null;
}
